import { useState, useEffect, useCallback } from 'react';

// useReleaseFilter — shared state for the Release Filter Bar.
//
// Maintains the current (op, value) selection. Persists to/restores from
// the URL query string so QA can share a link like
//   /admin/compat/errors?release_op=current
// and the recipient sees the same filter applied.
//
// API:
//   const {filter, setFilter, queryParams, days, setDays, layer, setLayer}
//        = useReleaseFilter();
//   - filter:      {op, value}
//                  op  ∈ all | current | pre_current | eq | ne | in
//                  value: string (only meaningful for eq/ne/in)
//   - queryParams: {release_op, release_value, days, layer}
//                  ready to spread into URLSearchParams or axios.params
//   - setFilter(partial): merges into current state; persists to URL
//   - days / setDays / layer / setLayer: convenience for the two extra
//                                         dimensions the bar exposes

const URL_KEYS = ['release_op', 'release_value', 'days', 'layer'];

function readFromUrl() {
    if (typeof window === 'undefined') return {};
    const sp = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of URL_KEYS) {
        const v = sp.get(k);
        if (v != null) out[k] = v;
    }
    return out;
}

function writeToUrl(state) {
    if (typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    // The default is 'current' (auto-scope to current build); only persist
    // an explicit override to the URL.
    if (state.op && state.op !== 'current') sp.set('release_op', state.op);
    else sp.delete('release_op');
    if (state.value && (state.op === 'eq' || state.op === 'ne' || state.op === 'in')) sp.set('release_value', state.value);
    else sp.delete('release_value');
    if (state.days != null) sp.set('days', String(state.days));
    else sp.delete('days');
    if (state.layer && state.layer !== 'all') sp.set('layer', state.layer);
    else sp.delete('layer');
    const qs = sp.toString();
    const newUrl = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState(null, '', newUrl);
}

export default function useReleaseFilter(initial = {}) {
    const fromUrl = readFromUrl();
    // Default to 'current' so every analytical page automatically scopes
    // to the build the fleet is mostly running. The current-version
    // resolver in backend/middleware/release_filter.js auto-picks the
    // highest app_version_code seen in the last 14 days, so a new release
    // hitting the wire becomes "current" without any server-side config.
    // Pages that want to show ALL releases (Source × Protocol Matrix,
    // Baseline Comparison) pass initial={op:'all'} explicitly.
    const [op, setOp]         = useState(fromUrl.release_op || initial.op || 'current');
    const [value, setValue]   = useState(fromUrl.release_value || initial.value || '');
    const [days, setDays]     = useState(fromUrl.days != null ? Number(fromUrl.days) : (initial.days || 30));
    const [layer, setLayer]   = useState(fromUrl.layer || initial.layer || 'all');

    useEffect(() => {
        writeToUrl({ op, value, days, layer });
    }, [op, value, days, layer]);

    const setFilter = useCallback((partial) => {
        if (partial.op    !== undefined) setOp(partial.op);
        if (partial.value !== undefined) setValue(partial.value);
    }, []);

    const queryParams = {};
    // Always send release_op to backend — even 'current' is now the
    // shared default. Only 'all' is omittable since the backend treats
    // missing op as 'all' for backwards compat.
    if (op && op !== 'all') queryParams.release_op = op;
    if (value && (op === 'eq' || op === 'ne' || op === 'in')) queryParams.release_value = value;
    if (days)  queryParams.days = days;
    if (layer && layer !== 'all') queryParams.layer = layer;

    return {
        filter: { op, value },
        setFilter,
        days,    setDays,
        layer,   setLayer,
        queryParams,
    };
}
